(function (global) {
  'use strict';
  if (global.ProfintCameraZoom) return;
  var controllers = new WeakMap(), byVideo = new WeakMap(), active = new Set();
  var clamp = function (value) { return Math.max(1, Math.min(3, Math.round(Number(value) * 10) / 10 || 1)); };
  var bounded = function (promise, milliseconds) {
    var timer;
    return Promise.race([promise, new Promise(function (_, reject) { timer = global.setTimeout(function () { reject(new Error('Camera zoom timed out.')); }, milliseconds); })])
      .finally(function () { global.clearTimeout(timer); });
  };

  function attach(host, video) {
    if (!host || !video || !video.srcObject) return null;
    var existing = controllers.get(host);
    if (existing && existing.video === video) return existing;
    existing?.stop();
    var original = video.srcObject;
    var track = original.getVideoTracks?.()[0] || original.getTracks?.().find(function (item) { return item.kind === 'video'; }) || original.getTracks?.()[0];
    if (!track) return null;
    var panel = video.closest('[data-lic-camera-panel], [data-camera], .lt-dni-capture__camera') || video.parentElement?.parentElement;
    var controls = panel?.querySelector('.public-camera-actions, .la-photo__camera-controls');
    if (!controls) {
      var button = panel?.querySelector('[data-lic-capture-camera], [data-take]');
      controls = button?.parentElement;
    }
    if (!controls) return null;
    controls.setAttribute('data-camera-zoom-controls', '');
    var control = document.createElement('div');
    control.className = 'profint-camera-zoom'; control.dataset.cameraZoom = '';
    control.innerHTML = '<button type="button" data-camera-zoom-out aria-label="Alejar la cámara" title="Alejar">−</button>'
      + '<input type="range" data-camera-zoom-range min="1" max="3" step="0.1" value="1" aria-label="Acercamiento de la cámara">'
      + '<button type="button" data-camera-zoom-in aria-label="Acercar la cámara" title="Acercar">+</button>'
      + '<output data-camera-zoom-value aria-live="polite">1x</output>';
    controls.prepend(control);
    var slider = control.querySelector('input'), output = control.querySelector('output');
    var minus = control.querySelector('[data-camera-zoom-out]'), plus = control.querySelector('[data-camera-zoom-in]');
    var capabilities; try { capabilities = track.getCapabilities?.().zoom; } catch (_) { /* Browser without camera capabilities. */ }
    var settings; try { settings = track.getSettings?.(); } catch (_) { /* Settings are optional. */ }
    var baseline = Number(settings?.zoom) || Number(capabilities?.min) || 1;
    var hardwareValue = baseline;
    var hardwareInFlight = null;
    var canHardware = Boolean(capabilities && typeof track.applyConstraints === 'function' && Number(capabilities.max) > Number(capabilities.min));
    var canvas = document.createElement('canvas');
    var canDigital = typeof canvas.captureStream === 'function';
    var current = 1, desired = 1, revision = 0, stopped = false, pending = false, queue = Promise.resolve();
    var source = null, context = null, derived = null, digitalFactor = 1, frame = null, callbackType = '';
    var observer;

    function show(value) {
      slider.value = String(value); output.textContent = String(Math.round(value * 10) / 10) + 'x';
      slider.setAttribute('aria-valuetext', output.textContent);
      minus.disabled = stopped || value <= 1 || (!canHardware && !canDigital);
      plus.disabled = stopped || value >= 3 || (!canHardware && !canDigital);
    }
    function invalidateGuides() {
      delete host.dataset.profintFaceCrop; delete host.dataset.profintFaceDetectedAt;
      host.dispatchEvent(new CustomEvent('profint:capture:zoom-changing', { bubbles: true }));
    }
    function changed() {
      if (!stopped) host.dispatchEvent(new CustomEvent('profint:capture:zoom-changed', { bubbles: true, detail: { zoom: current, mode: host.dataset.profintCameraZoomState, width: video.videoWidth, height: video.videoHeight } }));
    }
    function actualHardware() {
      try { var value = Number(track.getSettings?.().zoom); if (value > 0) hardwareValue = value; } catch (_) { /* Keep the last accepted setting. */ }
      return hardwareValue;
    }
    async function setHardware(value) {
      var timedOut = false;
      var operation = Promise.resolve(track.applyConstraints({ advanced: [{ zoom: value }] }));
      hardwareInFlight = operation;
      var result = operation.then(function () { hardwareValue = value; return actualHardware(); }, function (error) { canHardware = false; throw error; });
      // A timeout cannot cancel a camera driver. Keep it tracked and reconcile
      // only after it settles, rather than adding an unsafe second digital zoom.
      result.finally(function () {
        if (hardwareInFlight === operation) hardwareInFlight = null;
        if (timedOut && !stopped) void setZoom(desired);
      }).catch(function () { /* The awaited result below reports driver errors. */ });
      try { return await bounded(result, 1800); }
      catch (error) { if (hardwareInFlight === operation) { timedOut = true; error.cameraZoomInFlight = true; } throw error; }
    }
    function paint() {
      if (stopped || !source || !context || !source.videoWidth || !source.videoHeight) return;
      var nativeWidth = source.videoWidth, nativeHeight = source.videoHeight;
      // Crop physical pixels first; never interpolate a zoomed crop back to the
      // full native size. Capture and quality checks see the remaining resolution.
      var cropWidth = nativeWidth / digitalFactor, cropHeight = nativeHeight / digitalFactor;
      var scale = Math.min(1, 2400 / Math.max(cropWidth, cropHeight));
      var width = Math.max(1, Math.floor(cropWidth * scale)), height = Math.max(1, Math.floor(cropHeight * scale));
      if (canvas.width !== width || canvas.height !== height) { canvas.width = width; canvas.height = height; }
      context.drawImage(source, (nativeWidth - cropWidth) / 2, (nativeHeight - cropHeight) / 2, cropWidth, cropHeight, 0, 0, width, height);
      derived?.getVideoTracks?.()[0]?.requestFrame?.();
    }
    function schedule() {
      if (stopped || !source) return;
      if (typeof source.requestVideoFrameCallback === 'function') {
        callbackType = 'video'; frame = source.requestVideoFrameCallback(function () { paint(); schedule(); });
      } else {
        callbackType = 'animation'; frame = global.requestAnimationFrame(function () { paint(); schedule(); });
      }
    }
    async function decoded(width, height) {
      await bounded(new Promise(function (resolve) {
        var attempts = 0;
        function check() {
          if (stopped || (video.videoWidth === width && video.videoHeight === height && video.readyState >= 2)) return resolve();
          if (++attempts > 100) return resolve();
          global.setTimeout(check, 16);
        }
        check();
      }), 1800);
      if (!stopped && (video.videoWidth !== width || video.videoHeight !== height || video.readyState < 2)) throw new Error('Zoom frame is not ready.');
    }
    async function useDigital(factor) {
      if (!canDigital) throw new Error('Digital camera zoom is unavailable.');
      digitalFactor = Math.max(1, factor);
      if (!source) {
        source = document.createElement('video'); source.muted = true; source.playsInline = true;
        source.setAttribute('aria-hidden', 'true'); source.dataset.cameraZoomSource = ''; source.hidden = true;
        source.srcObject = original; host.append(source);
        await bounded(Promise.resolve(source.play()), 1800);
        if (stopped) return;
        context = canvas.getContext('2d', { alpha: false });
        if (!context || !source.videoWidth || !source.videoHeight) throw new Error('Camera pixels are not ready.');
        paint(); derived = canvas.captureStream(24);
        video.srcObject = derived;
        await bounded(Promise.resolve(video.play()), 1800);
        schedule();
      } else paint();
      await decoded(canvas.width, canvas.height);
    }
    async function resetDigital() {
      if (frame !== null) {
        if (callbackType === 'video') source?.cancelVideoFrameCallback?.(frame);
        else global.cancelAnimationFrame(frame);
        frame = null;
      }
      if (video.srcObject === derived) video.srcObject = original;
      derived?.getTracks?.().forEach(function (item) { item.stop(); }); derived = null;
      source?.pause(); if (source) { source.srcObject = null; source.remove(); }
      source = context = null; digitalFactor = 1; canvas.width = canvas.height = 0;
      if (!stopped) { try { await bounded(Promise.resolve(video.play()), 1800); } catch (_) { /* The original stream remains caller-owned. */ } }
      current = clamp(actualHardware() / baseline);
    }
    function setZoom(value) {
      if (stopped) return Promise.resolve(1);
      desired = clamp(value); var target = desired, observed = ++revision;
      pending = true;
      show(target); invalidateGuides();
      output.textContent = '…'; slider.setAttribute('aria-valuetext', 'Ajustando acercamiento');
      control.setAttribute('aria-busy', 'true');
      queue = queue.catch(function () { /* A previous unavailable request must not block subsequent attempts. */ }).then(async function () {
        if (stopped || observed !== revision) return current;
        if (hardwareInFlight) { host.dataset.profintCameraZoomState = 'pending'; return current; }
        var acceptedHardware = false;
        try {
          var nativeTarget = baseline * target;
          if (canHardware && nativeTarget >= Number(capabilities.min) && nativeTarget <= Number(capabilities.max)) {
            try {
              var tolerance = Math.max(.05, Number(capabilities.step) || .05);
              var actual = actualHardware();
              if (Math.abs(actual - nativeTarget) > tolerance) actual = await setHardware(nativeTarget);
              acceptedHardware = Math.abs(actual - nativeTarget) <= Math.max(.05, Number(capabilities.step) || .05);
            } catch (error) { if (error.cameraZoomInFlight) throw error; canHardware = false; }
          }
          if (stopped) return 1;
          if (acceptedHardware) {
            digitalFactor = 1;
            if (source) await useDigital(1);
            if (stopped) return 1;
            host.dataset.profintCameraZoomState = 'hardware';
          } else {
            // Reset optical zoom before a central digital crop. If a driver
            // keeps another setting, account for it instead of doubling zoom.
            if (actualHardware() !== baseline && typeof track.applyConstraints === 'function') {
              try { await setHardware(baseline); } catch (error) { if (error.cameraZoomInFlight) throw error; /* Read the actual setting below. */ }
            }
            var remaining = target / (actualHardware() / baseline);
            if (remaining < .99) throw new Error('The camera could not restore its original framing.');
            await useDigital(remaining);
            if (stopped) return 1;
            host.dataset.profintCameraZoomState = 'digital';
          }
          if (!stopped) { current = target; control.title = ''; }
        } catch (_) {
          if (!stopped) {
            if (hardwareInFlight) { host.dataset.profintCameraZoomState = 'pending'; control.title = 'La cámara está ajustando el acercamiento. Esperá un momento o cerrala para continuar.'; }
            else {
              if (source) await resetDigital();
              if (stopped) return 1;
              control.title = 'Esta cámara no admite ese acercamiento. Podés acercar el documento físicamente.';
              if (!derived && !canHardware) { canDigital = false; slider.disabled = true; host.dataset.profintCameraZoomState = 'unavailable'; }
            }
          }
        } finally {
          if (!stopped && observed === revision && !hardwareInFlight) { pending = false; show(current); control.removeAttribute('aria-busy'); changed(); }
        }
        return current;
      });
      return queue;
    }
    function stop() {
      if (stopped) return;
      stopped = true; pending = false; revision++; current = desired = 1;
      if (frame !== null) {
        if (callbackType === 'video') source?.cancelVideoFrameCallback?.(frame);
        else global.cancelAnimationFrame(frame);
      }
      derived?.getTracks?.().forEach(function (item) { item.stop(); });
      source?.pause(); if (source) { source.srcObject = null; source.remove(); }
      original.getTracks?.().forEach(function (item) { if (item.readyState === 'live') item.stop(); });
      control.remove(); observer?.disconnect();
      controls.removeAttribute('data-camera-zoom-controls');
      if (video.srcObject === derived) video.srcObject = null;
      delete host.dataset.profintCameraZoomState;
      delete host.dataset.profintFaceCrop; delete host.dataset.profintFaceDetectedAt;
      controllers.delete(host); byVideo.delete(video); active.delete(controller);
      // Detached hosts cannot bubble their close event to the shared bridge.
      global.ProfintCameraQuality?.stopGuides?.(host);
    }
    var controller = { video: video, setZoom: setZoom, getZoom: function () { return current; }, isPending: function () { return pending; }, stop: stop,
      whenReady: async function () {
        var pending; do { pending = queue; await pending; } while (!stopped && pending !== queue);
        if (!stopped && hardwareInFlight) throw new Error('La cámara está ajustando el acercamiento. Esperá un momento y reintentá.');
      } };
    controllers.set(host, controller); byVideo.set(video, controller); active.add(controller);
    host.dataset.profintCameraZoomState = 'idle';
    if (!canHardware && !canDigital) { slider.disabled = true; control.title = 'El zoom no está disponible en este navegador.'; host.dataset.profintCameraZoomState = 'unavailable'; }
    show(1);
    slider.addEventListener('input', function () { void setZoom(slider.value); });
    minus.addEventListener('click', function () { void setZoom(desired - .1); });
    plus.addEventListener('click', function () { void setZoom(desired + .1); });
    observer = new MutationObserver(function () { if (!host.isConnected) stop(); });
    observer.observe(document.body, { childList: true, subtree: true });
    return controller;
  }
  global.ProfintCameraZoom = { attach: attach, stop: function (host) { controllers.get(host)?.stop(); },
    isPending: function (video) { return byVideo.get(video)?.isPending() || false; },
    whenReady: function (video) { return byVideo.get(video)?.whenReady() || Promise.resolve(); } };
  document.addEventListener('profint:capture:camera-ready', function (event) {
    var host = event.target, video = host?.querySelector?.('[data-lic-camera-video]');
    if (video) attach(host, video);
  }, true);
  document.addEventListener('profint:capture:camera-closed', function (event) { controllers.get(event.target)?.stop(); }, true);
  global.addEventListener('pagehide', function () { Array.from(active).forEach(function (controller) { controller.stop(); }); });
})(window);
